import { useState } from 'react';
import { FileText, Download, File as FileIcon, Loader2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDecryptedMedia } from '@/hooks/useDecryptedMedia';

interface FileAttachmentProps {
  url: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  isOwn?: boolean;
  /** Per-file decryption key for Signal-grade encrypted media */
  fileKey?: string | null;
}

export function FileAttachment({ url, fileName, fileSize, fileType, isOwn, fileKey }: FileAttachmentProps) {
  const { decryptedUrl, isDecrypting, error } = useDecryptedMedia(url, fileKey, fileType || 'application/octet-stream');
  const [isDownloading, setIsDownloading] = useState(false);

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isPdf = fileType.includes('pdf');

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isDownloading || !decryptedUrl) return;
    setIsDownloading(true);
    try {
      const response = await fetch(decryptedUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Download failed:', err);
      window.open(decryptedUrl, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  if (error) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-destructive/10 text-destructive text-xs">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Failed to decrypt file</span>
      </div>
    );
  }

  return (
    <motion.button
      type="button"
      onClick={handleDownload}
      whileTap={{ scale: 0.97 }}
      disabled={isDecrypting}
      className={`flex items-center gap-3 p-3 rounded-2xl w-full text-left ${
        isOwn ? 'bg-primary-foreground/10' : 'bg-background/60'
      }`}
    >
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
        isOwn ? 'bg-primary-foreground/15' : 'bg-muted'
      }`}>
        {isDecrypting ? (
          <Loader2 className={`h-5 w-5 animate-spin ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`} />
        ) : isPdf ? (
          <FileText className={`h-5 w-5 ${isOwn ? 'text-primary-foreground/70' : 'text-primary'}`} />
        ) : (
          <FileIcon className={`h-5 w-5 ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[13px] font-medium truncate ${isOwn ? 'text-primary-foreground' : 'text-foreground'}`}>
          {isDecrypting ? 'Decrypting...' : fileName}
        </p>
        <p className={`text-[11px] ${isOwn ? 'text-primary-foreground/50' : 'text-muted-foreground/60'}`}>
          {formatSize(fileSize)}
        </p>
      </div>
      {isDownloading ? (
        <Loader2 className={`h-4 w-4 shrink-0 animate-spin ${isOwn ? 'text-primary-foreground/40' : 'text-muted-foreground/40'}`} />
      ) : (
        <Download className={`h-4 w-4 shrink-0 ${isOwn ? 'text-primary-foreground/40' : 'text-muted-foreground/40'}`} />
      )}
    </motion.button>
  );
}
